import React, {
  useCallback,
  useEffect,
  useContext,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { Button, Popover } from 'grab-ui';
import _get from 'lodash/get';
import excelSvg from 'public/icons/bulkupload/excel.svg';
import helpSvg from 'public/icons/bulkupload/help.svg';
import I18n from 'i18n-js';
import UploadSpreadSheetModal from 'components/v2/anytime-booking/bulk-uploader';
import { bulkUploadMetaDataSelector } from 'selectors/new-bulk-upload-selectors';
import { bookingConfigurationSelector } from 'selectors/new-booking-selectors';
import cx from 'classnames';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import {
  getUserCountryCodeFromState,
  itemCategoriesSelector,
  selectedCitySelector,
  featureFlagsSelector,
} from 'selectors/app-selector';
import {
  MAX_EXCEL_SIZE,
  PARSE_EXCEL_ERROR_CODE,
} from 'constants/new-bulk-upload-const';
import { pushUploadExcelError } from 'components/v2/anytime-booking/bulk-uploader/helper';
import { parseUploadExcel } from 'components/v2/anytime-booking/bulk-uploader/new-bulk-upload-parser';

import { getCurrentMetaDataVersion, setMetaDataVersion } from 'helper/storage';
import { downloadExcelTemplate } from 'components/v2/anytime-booking/bulk-uploader/new-bulk-upload-generator';
import { pushNotificationAction } from 'actions/app-actions';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import styles from './styles.less';
import BulkUploadPageContext from '../common/context';
import { BulkUploadOnboardingModal } from './bur-onboarding-modal';

import { HelperContent } from './pop-up';

const NEW_TEMPLATE_ONBOARDING_KEY = 'district';
const NEW_TEMPLATE_ONBOARDING_TIME = 3;
const NEW_TEMPLATE_ONBOARDING_KEY_FORMAT = `__new_bur_template-${NEW_TEMPLATE_ONBOARDING_KEY}__`;

const UploadFile = ({ metaData, city, countryCode, dispatch }) => {
  const { stepForwardFunc } = useContext(BulkUploadPageContext);
  const [showDownloadHelperPopup, setShowDownloadHelperPopup] = useState(false);
  const [showNewTemplateOnboarding, setShowNewTemplateOnboarding] = useState(
    false,
  );
  const currentMetaVersion = getCurrentMetaDataVersion();
  const showNewTemplate = metaData && currentMetaVersion !== metaData.version;
  const adminAreasExists = !!_get(metaData, 'meta.adminAreas.length');
  const bookingConfiguration = useSelector(bookingConfigurationSelector);

  const featureFlags = useSelector(featureFlagsSelector);

  useEffect(
    () => {
      dispatch(NewBulkUploadDuck.creators.initBulkUploadJob());
    },
    [dispatch],
  );

  useEffect(
    () => {
      if (!adminAreasExists) {
        return;
      }

      const strCount = window.localStorage.getItem(
        NEW_TEMPLATE_ONBOARDING_KEY_FORMAT,
      );
      const intCount = Number.isNaN(strCount) ? 0 : +strCount;
      if (intCount < NEW_TEMPLATE_ONBOARDING_TIME) {
        window.localStorage.setItem(
          NEW_TEMPLATE_ONBOARDING_KEY_FORMAT,
          `${intCount + 1}`,
        );
        setShowDownloadHelperPopup(true);
        setShowNewTemplateOnboarding(true);
      }
    },
    [adminAreasExists, setShowDownloadHelperPopup],
  );

  useEffect(
    () => {
      if (city && !metaData) {
        dispatch(NewBulkUploadDuck.creators.fetchMetaData());
      }
    },
    [city, metaData],
  );

  const itemCategories = useSelector(itemCategoriesSelector);

  const onDownloadExcelTemplate = useCallback(
    () => {
      downloadExcelTemplate(
        metaData,
        itemCategories,
        bookingConfiguration,
        featureFlags,
      );
      if (currentMetaVersion !== metaData.version) {
        setMetaDataVersion(metaData.version);
      }
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep1DownloadTemplate(),
      );
    },
    [
      metaData,
      countryCode,
      currentMetaVersion,
      itemCategories,
      bookingConfiguration,
      featureFlags,
    ],
  );

  const uploadSuccessToast = useCallback(
    () => {
      dispatch(
        pushNotificationAction({
          id: 'successUploadFile',
          type: NOTIFICATION_TYPE.CONFIRMED,
          message: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_heading_file_uploaded_happypath',
          ),
          description: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_check_info_before_proceeding_happypath',
          ),
        }),
      );
    },
    [dispatch],
  );

  const uploadExcelFunc = useCallback(
    file => {
      if (file && file.size > MAX_EXCEL_SIZE) {
        pushUploadExcelError(dispatch, {
          errorCode: PARSE_EXCEL_ERROR_CODE.FILE_SIZE_ERROR,
        });
      } else {
        const fileReader = new FileReader();
        fileReader.onload = async event => {
          try {
            const ExcelModule = await import('exceljs/dist/exceljs');
            const Excel = ExcelModule.default;

            const arrayBuffer = fileReader.result;
            const workbook = new Excel.Workbook();
            workbook.xlsx.load(arrayBuffer).then(() => {
              try {
                const parsedRowList = parseUploadExcel({
                  workbook,
                  metaDataWithVersion: metaData,
                  bookingConfiguration,
                  featureFlags,
                });
                dispatch(NewBulkUploadDuck.creators.addRawData(parsedRowList));
                dispatch(
                  scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep1SuccessUpload(),
                );
                stepForwardFunc();
                uploadSuccessToast();
              } catch (error) {
                pushUploadExcelError(dispatch, error);
              }
            });
          } catch (error) {
            pushUploadExcelError(dispatch, {
              errorCode: PARSE_EXCEL_ERROR_CODE.PARSE_ERROR,
              error,
            });
          }
        };
        fileReader.readAsArrayBuffer(file);
      }
    },
    [metaData, bookingConfiguration, dispatch, stepForwardFunc],
  );

  const toggleDownloadHelperPopUp = useCallback(
    () => {
      setShowDownloadHelperPopup(!showDownloadHelperPopup);
    },
    [setShowDownloadHelperPopup, showDownloadHelperPopup],
  );

  const helperContent = useMemo(
    () =>
      showNewTemplateOnboarding ? (
        <HelperContent
          title={I18n.t('v2.bulkUpload.newTemplateOnboarding.whatsNew')}
          subtitle={I18n.t(
            `v2.bulkUpload.newTemplateOnboarding.${NEW_TEMPLATE_ONBOARDING_KEY}.title`,
          )}
          desc={I18n.t(
            `v2.bulkUpload.newTemplateOnboarding.${NEW_TEMPLATE_ONBOARDING_KEY}.description`,
          )}
          closeFunc={toggleDownloadHelperPopUp}
        />
      ) : (
        <HelperContent
          title={I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_heading_book_using_template_nudges_notif_loading_screen',
          )}
          desc={I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_fillin_delivery_info_nudges_notif_loading_screen',
          )}
          closeFunc={toggleDownloadHelperPopUp}
        />
      ),
    [showNewTemplateOnboarding, toggleDownloadHelperPopUp],
  );

  return (
    <div className={styles.uploadContainer}>
      <p className={styles.title}>
        {I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_body_upload_delivery_details_happypath',
        )}
      </p>
      <BulkUploadOnboardingModal />
      <UploadSpreadSheetModal
        disabled={!metaData}
        uploadExcelFunc={uploadExcelFunc}
      />
      <div className={cx(styles.downloadSection)}>
        <div className={styles.highlightDownloadButton} id={'highLightSection'}>
          <Button
            className={cx(styles.downloadXLSButton, {
              [styles.disabled]: !metaData,
            })}
            onClick={onDownloadExcelTemplate}
            disabled={!metaData}
            id={'downloadXLSButton'}
          >
            <img
              className={cx(styles.excelIcon, {
                [styles.disabled]: !metaData,
              })}
              src={excelSvg}
              alt=""
            />
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_button_dwnload_xlsx_template_happypath',
            )}
          </Button>
          <div
            className={cx(styles.newIcon, {
              [styles.hide]: !showNewTemplate,
            })}
          >
            <span className={styles.text}>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_label_new_nudges_notif_loading_screen',
              )}
            </span>
          </div>
        </div>
        <Popover
          visible={!!metaData && showDownloadHelperPopup}
          placement={'right'}
          overlayClassName={styles.downloadHelper}
          content={helperContent}
        >
          <div onClick={toggleDownloadHelperPopUp}>
            <img className={styles.helpIcon} src={helpSvg} alt="" />
          </div>
        </Popover>
      </div>
    </div>
  );
};

UploadFile.defaultProps = {
  metaData: undefined,
  city: undefined,
  countryCode: undefined,
};

UploadFile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  city: PropTypes.shape({}),
  countryCode: PropTypes.string,
  metaData: PropTypes.shape({}),
};

export default connect(state => ({
  metaData: bulkUploadMetaDataSelector(state),
  city: selectedCitySelector(state),
  countryCode: getUserCountryCodeFromState(state),
}))(UploadFile);
